import * as Comlink from "comlink";
import { getSupabaseAccessToken } from "@/lib/supabase";
import type { AgentConfig, AgentEvent, LLMMessage, WorkerAPI } from "./types";

/**
 * Bridge between the main thread (React) and the agent Web Worker.
 * Manages Worker lifecycle and provides a typed API via Comlink.
 */
export class AgentWorkerBridge {
  private worker: Worker | null = null;
  private api: Comlink.Remote<WorkerAPI> | null = null;
  private userId: string | null = null;
  // Resolves when the current worker is terminated. Comlink never settles
  // calls to a dead worker, so run calls race this -- otherwise a run cut
  // off by a user switch would leave its caller (and isStarting) pending
  // forever.
  private terminated: Promise<void> = Promise.resolve();
  private signalTerminated: () => void = () => {};

  /**
   * Lazily create the Worker on first use.
   */
  private ensureWorker(): Comlink.Remote<WorkerAPI> {
    if (!this.api || !this.worker) {
      console.log("[AgentWorkerBridge] Creating new Worker...");
      this.worker = new Worker(
        new URL("./worker.ts", import.meta.url),
        { type: "module" },
      );

      // Restart on crash
      this.worker.addEventListener("error", (event) => {
        console.error("[AgentWorkerBridge] Worker error:", event.message, event);
        this.terminate();
      });

      this.worker.addEventListener("messageerror", (event) => {
        console.error("[AgentWorkerBridge] Worker messageerror:", event);
      });

      this.api = Comlink.wrap<WorkerAPI>(this.worker);
      this.terminated = new Promise((resolve) => {
        this.signalTerminated = resolve;
      });
      console.log("[AgentWorkerBridge] Worker created and wrapped with Comlink");

      // Cross-thread auth: wire main-thread token getter into worker once.
      // The worker calls back through this proxy whenever LLMClient needs a JWT.
      this.api.setAccessTokenGetter(Comlink.proxy(getSupabaseAccessToken));
      // Scope the worker's IndexedDB to the current user. Comlink delivers
      // messages in order, so this lands before any run the caller starts.
      this.api.setUserId(this.userId);
    }
    return this.api;
  }

  /**
   * Start a new agent run.
   */
  async startRun(
    runId: string,
    config: AgentConfig,
    prompt: string,
    onEvent: (event: AgentEvent) => void,
  ): Promise<void> {
    console.log("[AgentWorkerBridge] startRun called", { runId });
    const api = this.ensureWorker();
    console.log("[AgentWorkerBridge] calling api.startRun via Comlink...");
    return this.untilTerminated(api.startRun(runId, config, prompt, Comlink.proxy(onEvent)));
  }

  /**
   * Continue a completed run with a new user message (multi-turn).
   */
  async continueRun(
    runId: string,
    config: AgentConfig,
    message: string,
    onEvent: (event: AgentEvent) => void,
  ): Promise<void> {
    console.log("[AgentWorkerBridge] continueRun called", { runId });
    const api = this.ensureWorker();
    return this.untilTerminated(api.continueRun(runId, config, message, Comlink.proxy(onEvent)));
  }

  /**
   * Resume a paused run with updated messages.
   */
  async resumeRun(
    runId: string,
    config: AgentConfig,
    messages: LLMMessage[],
    onEvent: (event: AgentEvent) => void,
  ): Promise<void> {
    const api = this.ensureWorker();
    return this.untilTerminated(api.resumeRun(runId, config, messages, Comlink.proxy(onEvent)));
  }

  /**
   * Cancel an active run.
   */
  async cancelRun(runId: string): Promise<void> {
    if (!this.api) return;
    return this.api.cancelRun(runId);
  }

  /**
   * Switch the user the worker acts for. On a change the worker is torn
   * down: that cancels any in-flight run (which would otherwise keep
   * streaming the old user's output into the new user's chat) and drops the
   * worker's in-memory conversations. The next run spawns a fresh worker
   * scoped to the new user.
   */
  setUserId(userId: string | null): void {
    if (userId === this.userId) return;
    this.userId = userId;
    this.terminate();
  }

  /**
   * Terminate the Worker completely.
   */
  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
      this.api = null;
      this.signalTerminated();
    }
  }

  private untilTerminated(call: Promise<void>): Promise<void> {
    return Promise.race([call, this.terminated]);
  }
}

/** Singleton bridge instance */
let _bridge: AgentWorkerBridge | null = null;

export function getAgentBridge(): AgentWorkerBridge {
  if (!_bridge) {
    _bridge = new AgentWorkerBridge();
  }
  return _bridge;
}
